<template>
  <div class="dashboard-home">
    <!-- Welcome Banner -->
    <div class="welcome-banner">
      <div class="welcome-text">
        <h1>خوش آمدید، والد گرامی 👋</h1>
        <p>این مرکز کنترل شماست. فعالیت‌های کودک را بررسی کنید و استعدادهایش را کشف کنید.</p>
      </div>
      <div class="welcome-action">
        <button class="btn-create-glow" @click="$router.push('/dashboard/rooms')">
          <span>+ ساخت اتاق جدید</span>
        </button>
      </div>
    </div>

    <!-- Stats Grid -->
    <div class="stats-overview">
      <div class="stat-card blue-gradient">
        <div class="stat-icon">🎮</div>
        <div class="stat-info">
          <span class="stat-label">اتاق‌های فعال</span>
          <strong class="stat-value">{{ activeRooms.length }}</strong>
        </div>
        <div class="stat-chart">
          <!-- Mini sparkline decoration -->
          <svg viewBox="0 0 100 30" class="sparkline"><path d="M0,30 Q30,5 50,15 T100,5" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="3"/></svg>
        </div>
      </div>
      <div class="stat-card green-gradient">
        <div class="stat-icon">👀</div>
        <div class="stat-info">
          <span class="stat-label">بازدید کل</span>
          <strong class="stat-value">{{ totalVisits }}</strong>
        </div>
        <div class="stat-chart">
          <svg viewBox="0 0 100 30" class="sparkline"><path d="M0,30 L20,20 L40,25 L60,10 L80,15 L100,0" fill="none" stroke="rgba(255,255,255,0.5)" stroke-width="3"/></svg>
        </div>
      </div>
      <div class="stat-card purple-gradient">
        <div class="stat-icon">🎨</div>
        <div class="stat-info">
          <span class="stat-label">آثار هنری</span>
          <strong class="stat-value">--</strong>
        </div>
        <div class="stat-chart">
          <div class="dots"><span/><span/><span/></div>
        </div>
      </div>
    </div>

    <div class="main-grid">
      <!-- Left Column: Rooms -->
      <div class="col-left">
        <div class="section-header">
          <h3>اتاق‌های بازی من</h3>
          <router-link to="/dashboard/rooms" class="link-more">مشاهده همه ›</router-link>
        </div>

        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>در حال بارگذاری...</p>
        </div>

        <div v-else-if="activeRooms.length === 0" class="empty-state">
          <div class="empty-illustration">🎈</div>
          <h4>هنوز اتاقی نساخته‌اید</h4>
          <p>برای شروع ماجراجویی، یک اتاق بازی امن برای کودک خود بسازید.</p>
          <button class="btn-primary" @click="$router.push('/dashboard/rooms')">ساخت اولین اتاق</button>
        </div>

        <div v-else class="rooms-list">
          <div v-for="room in activeRooms" :key="room.slug" class="room-row" @click="$router.push(`/dashboard/rooms/${room.slug}`)">
            <div class="room-icon-box">🎮</div>
            <div class="room-content">
              <h4>{{ room.name }}</h4>
              <span class="sub-text">کودک: {{ room.childName }}</span>
            </div>
            <div class="room-metrics">
              <div class="metric">
                <span class="m-val">{{ room.visits }}</span>
                <span class="m-lbl">بازدید</span>
              </div>
            </div>
            <div class="room-status">
              <span class="badge active">{{ formatDate(room.endAt) }}</span>
            </div>
            <div class="arrow">›</div>
          </div>
        </div>
      </div>

      <!-- Right Column: Analytics/Tips -->
      <div class="col-right">
        <!-- Analytics Placeholder -->
        <div class="analytics-card">
          <div class="card-header">
            <h4>تحلیل هفته</h4>
            <span class="badge-pro">PRO</span>
          </div>
          <div class="chart-box">
            <div class="bar-chart">
              <div class="bar" style="height: 40%"><span>ش</span></div>
              <div class="bar" style="height: 70%"><span>ی</span></div>
              <div class="bar" style="height: 50%"><span>د</span></div>
              <div class="bar" style="height: 90%; background: var(--accent);"><span>س</span></div>
              <div class="bar" style="height: 60%"><span>چ</span></div>
              <div class="bar" style="height: 30%"><span>پ</span></div>
              <div class="bar" style="height: 50%"><span>ج</span></div>
            </div>
          </div>
          <p class="insight-text">فعالیت کودک در روز <strong>سه‌شنبه</strong> بیشترین مقدار بوده است.</p>
        </div>

        <div class="tips-section">
          <h4>راهنمای هوشمند</h4>
          <div class="tip-box">
            <div class="tip-icon">💡</div>
            <div class="tip-body">
              <strong>امنیت اتاق‌ها</strong>
              <p>با تنظیم پین‌کد یا الگو، دسترسی به بازی‌ها را مدیریت کنید.</p>
            </div>
          </div>
          <div class="tip-box">
            <div class="tip-icon">🧠</div>
            <div class="tip-body">
              <strong>تحلیل نقاشی</strong>
              <p>هوش مصنوعی ما نقاشی‌های کودک را تحلیل می‌کند و به شما گزارش می‌دهد.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { ROOMS_MINE_QUERY } from '../api/queries'

const { result, loading } = useQuery(ROOMS_MINE_QUERY, null, { fetchPolicy: 'cache-and-network' })

const activeRooms = computed(() => {
  return (result.value?.roomsMine || []).filter(r => r.status === 'active')
})

const totalVisits = computed(() => {
  return (result.value?.roomsMine || []).reduce((acc, r) => acc + r.visits, 0)
})

function formatDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const now = new Date()
  const diffDays = Math.ceil((d - now) / (1000 * 60 * 60 * 24))
  
  if (diffDays < 0) return 'منقضی'
  if (diffDays === 0) return 'امروز'
  return `${diffDays} روز مانده`
}
</script>

<style scoped>
.dashboard-home {
  max-width: 1200px;
  margin: 0 auto;
}

/* Welcome Banner */
.welcome-banner {
  background: linear-gradient(120deg, #2563eb 0%, #1e40af 100%);
  border-radius: 24px;
  padding: 32px 40px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 40px;
  box-shadow: 0 10px 30px rgba(37, 99, 235, 0.2);
  position: relative;
  overflow: hidden;
}
.welcome-banner::before {
  content: '';
  position: absolute;
  top: -50px;
  right: -50px;
  width: 200px;
  height: 200px;
  background: rgba(255,255,255,0.1);
  border-radius: 50%;
}
.welcome-text h1 { margin: 0 0 10px; font-size: 26px; font-weight: 800; }
.welcome-text p { margin: 0; opacity: 0.9; font-size: 15px; }

.btn-create-glow {
  background: rgba(255,255,255,0.2);
  border: 1px solid rgba(255,255,255,0.4);
  color: #fff;
  padding: 12px 24px;
  border-radius: 12px;
  font-weight: 700;
  cursor: pointer;
  backdrop-filter: blur(5px);
  transition: all 0.3s;
}
.btn-create-glow:hover { background: #fff; color: #2563eb; transform: translateY(-2px); }

/* Stats Overview */
.stats-overview {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 24px;
  margin-bottom: 40px;
}
.stat-card {
  border-radius: 20px;
  padding: 24px;
  color: #fff;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 20px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 160px;
}
.blue-gradient { background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%); box-shadow: 0 8px 20px rgba(59, 130, 246, 0.3); }
.green-gradient { background: linear-gradient(135deg, #10b981 0%, #059669 100%); box-shadow: 0 8px 20px rgba(16, 185, 129, 0.3); }
.purple-gradient { background: linear-gradient(135deg, #a855f7 0%, #7c3aed 100%); box-shadow: 0 8px 20px rgba(168, 85, 247, 0.3); }

.stat-icon { font-size: 32px; margin-bottom: 12px; opacity: 0.9; }
.stat-info { z-index: 2; }
.stat-label { font-size: 14px; opacity: 0.9; display: block; margin-bottom: 4px; }
.stat-value { font-size: 32px; font-weight: 800; }
.stat-chart { position: absolute; bottom: 0; right: 0; width: 100%; height: 60px; opacity: 0.3; pointer-events: none; }
.sparkline { width: 100%; height: 100%; }

/* Main Grid */
.main-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 32px;
}
@media (max-width: 900px) { .main-grid { grid-template-columns: 1fr; } }

/* Shared Headlines */
.section-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
.section-header h3 { font-size: 18px; font-weight: 700; color: #1e293b; margin: 0; }
.link-more { color: #3b82f6; text-decoration: none; font-size: 13px; font-weight: 600; }

/* Rooms List */
.rooms-list { display: flex; flex-direction: column; gap: 16px; }
.room-row {
  background: #fff;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  border: 1px solid #f1f5f9;
  transition: all 0.2s;
  cursor: pointer;
}
.room-row:hover { transform: translateX(-4px); border-color: #e2e8f0; box-shadow: 0 4px 12px rgba(0,0,0,0.03); }
.room-icon-box {
  width: 48px;
  height: 48px;
  background: #eff6ff;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  color: #3b82f6;
}
.room-content { flex: 1; }
.room-content h4 { margin: 0 0 4px; font-size: 16px; color: #1e293b; }
.sub-text { font-size: 13px; color: #64748b; }
.room-metrics { display: flex; gap: 16px; padding: 0 16px; border-left: 1px solid #f1f5f9; }
.metric { display: flex; flex-direction: column; align-items: center; }
.m-val { font-weight: 700; color: #334155; }
.m-lbl { font-size: 11px; color: #94a3b8; }
.badge { font-size: 12px; padding: 4px 10px; border-radius: 99px; font-weight: 600; }
.badge.active { background: #ecfdf5; color: #059669; }
.arrow { color: #cbd5e1; font-size: 20px; font-weight: 300; }

.empty-state {
  background: #fff;
  border: 2px dashed #e2e8f0;
  border-radius: 20px;
  text-align: center;
  padding: 40px;
}
.empty-illustration { font-size: 48px; margin-bottom: 16px; opacity: 0.5; }
.empty-state h4 { color: #1e293b; margin: 0 0 10px; }
.empty-state p { color: #64748b; margin-bottom: 20px; }
.btn-primary { background: #2563eb; color: #fff; border: none; padding: 10px 24px; border-radius: 99px; font-weight: 600; cursor: pointer; }

/* Right Col: Analytics */
.analytics-card {
  background: #fff;
  border-radius: 20px;
  padding: 24px;
  border: 1px solid #f1f5f9;
  margin-bottom: 32px;
}
.card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
.card-header h4 { margin: 0; color: #1e293b; font-size: 16px; }
.badge-pro { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); color: #fff; font-size: 10px; padding: 2px 6px; border-radius: 4px; font-weight: 800; }

.chart-box { height: 160px; display: flex; align-items: flex-end; justify-content: center; margin-bottom: 20px; }
.bar-chart { display: flex; gap: 8px; align-items: flex-end; height: 100%; width: 100%; }
.bar { flex: 1; background: #e2e8f0; border-radius: 6px 6px 0 0; position: relative; transition: height 0.5s; }
.bar span { position: absolute; bottom: -20px; width: 100%; text-align: center; font-size: 12px; color: #94a3b8; }
.insight-text { font-size: 13px; color: #475569; line-height: 1.6; background: #f8fafc; padding: 12px; border-radius: 8px; }

.tips-section h4 { font-size: 16px; color: #1e293b; margin: 0 0 16px; }
.tip-box { background: #fff; border: 1px solid #f1f5f9; border-radius: 16px; padding: 16px; display: flex; gap: 12px; margin-bottom: 16px; transition: transform 0.2s; }
.tip-box:hover { transform: translateY(-2px); border-color: #cbd5e1; }
.tip-icon { font-size: 24px; }
.tip-body strong { display: block; color: #334155; margin-bottom: 4px; font-size: 14px; }
.tip-body p { margin: 0; font-size: 12px; color: #64748b; line-height: 1.5; }

.loading-state { text-align: center; padding: 40px; color: #94a3b8; }
.spinner { width: 30px; height: 30px; border: 3px solid #e2e8f0; border-top-color: #3b82f6; border-radius: 50%; animation: spin 1s infinite linear; margin: 0 auto 10px; }
@keyframes spin { to { transform: rotate(360deg); } }
</style>
